import { ArrowLeftIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Flex,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { Activity } from '../../api/types';
import ActivityLogCard from '../../components/ActivityLogCard';
import { UserPermissions } from '../../configs/routes';
import { useUserContext } from '../../context/UserContextProvider';
import { isTestingEnvironment } from '../../helpers/environments';
import useAutomationActivityLogQuery from '../../hooks/useAutomationActivityLogQuery';
import { ContentLayout } from '../../layout/ContentLayout';
import PageMetatags from '../../layout/PageMetatags';
import AutomationStatusOverviewCard from './AutomationStatusOverviewCard';

const AutomationStatusOverviewPage = () => {
  const { t } = useTranslation();
  const { isMember } = useUserContext();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const activityLogQuery = useAutomationActivityLogQuery({ page, itemsPerPage });
  const lastPage = activityLogQuery.data?.meta?.last_page ?? 1;

  const pagination = useMemo(
    () =>
      lastPage > 1 ? (
        <Flex justifyContent="space-between" m={4} alignItems="center">
          <Flex>
            <Tooltip label={t('components:table.first')}>
              <IconButton
                onClick={() => setPage(1)}
                isDisabled={page === 1}
                icon={<ArrowLeftIcon h={3} w={3} />}
                mr={4}
                aria-label="first"
              />
            </Tooltip>
            <Tooltip label={t('components:table.previous')}>
              <IconButton
                onClick={() => setPage((p) => p - 1)}
                isDisabled={page === 1}
                icon={<ChevronLeftIcon h={6} w={6} />}
                aria-label="previous"
              />
            </Tooltip>
          </Flex>

          <Flex alignItems="center">
            <Text mr={8}>
              {t('components:table.page')}
              <Text fontWeight="bold" as="span">
                {page}
              </Text>{' '}
              {t('components:table.of')}
              <Text fontWeight="bold" as="span">
                {lastPage}
              </Text>
            </Text>
            <Text>{t('components:table.go_to_page')}</Text>{' '}
            <NumberInput
              key={page}
              ml={2}
              mr={8}
              w={28}
              min={1}
              max={lastPage}
              defaultValue={page}
              onChange={(value) => {
                const p = value ? Number(value) : 1;
                if (p >= 1 && p <= lastPage) setPage(p);
              }}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Select
              w={48}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {t('components:table.show')} {pageSize}
                </option>
              ))}
            </Select>
          </Flex>

          <Flex>
            <Tooltip label={t('components:table.next')}>
              <IconButton
                onClick={() => setPage((p) => p + 1)}
                isDisabled={page === lastPage}
                icon={<ChevronRightIcon h={6} w={6} />}
                aria-label="next"
              />
            </Tooltip>
            <Tooltip label={t('components:table.last')}>
              <IconButton
                onClick={() => setPage(lastPage)}
                isDisabled={page === lastPage}
                icon={<ArrowRightIcon h={3} w={3} />}
                ml={4}
                aria-label="last"
              />
            </Tooltip>
          </Flex>
        </Flex>
      ) : null,
    [page, lastPage, itemsPerPage, t],
  );

  if (!isTestingEnvironment() && !isMember(UserPermissions.AUTOMATIONSCONTROL)) {
    return <Navigate to="/app/op/dashboard" replace />;
  }

  return (
    <ContentLayout>
      <PageMetatags title={t('pages:automations.meta.title')} />
      <AutomationStatusOverviewCard />
      <ActivityLogCard
        activities={activityLogQuery.data?.data as Activity[] | undefined}
        queryKey={useAutomationActivityLogQuery.getKey(page, itemsPerPage)}
        resourceKey="automation"
        canSendMessage={false}
        showSubjectName={true}
        footer={pagination}
      />
    </ContentLayout>
  );
};

export default AutomationStatusOverviewPage;
